import axios from 'axios';
import { getUserConfig } from './user';
import { User } from 'ts/shared/interfaces/User';
import { showSuccess } from 'utils/utils';
import { CognitoResponse, GtprCredentialsResponse } from 'ts/shared/interfaces/Cognito';
import { loginHubUrl } from 'constants/constant';

const LAST_ACTIVITY_TIMESTAMP_KEY = 'lastActivityCognito';

let refreshPromise: Promise<boolean | undefined> | null = null;

const isBeproCognito = () => {
  const globals = localStorage.getItem('globals');
  if (!globals) {
    return false;
  }
  try {
    return !!JSON.parse(globals).useBeproCognito;
  } catch {
    return false;
  }
};

const applyAuthHeaderFromStorage = (config: any) => {
  if (!config.headers) {
    config.headers = {};
  }

  if (isBeproCognito()) {
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      if (userData.accessToken) {
        config.headers.Authorization = `Bearer ${userData.accessToken}`;
        return true;
      }
    }
    return false;
  }

  const cognitoData = localStorage.getItem('cognito');
  if (cognitoData) {
    const { access_token, token_type } = JSON.parse(cognitoData);
    if (access_token) {
      config.headers.Authorization = `${token_type} ${access_token}`;
      return true;
    }
  }
  return false;
};

export const refreshTokenSingleFlight = () => {
  if (!refreshPromise) {
    refreshPromise = refreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

export const connectToDefaultUser = async () => {
  const cognitoDefault = localStorage.getItem('cognito');

  if (cognitoDefault) {
    return JSON.parse(cognitoDefault);
  }
  try {
    const url = 'Account/ConnectCognitoUserToDefaultBeproUser';
    const res = await axios.post<CognitoResponse>(url);
    if (res.data.hasErrors) {
      return undefined;
    }
    localStorage.setItem('cognito', JSON.stringify(res.data));
    return res.data;
  } catch (error) {
    return undefined;
  }
};

export const configureAxiosInterceptors = async (maxInactiveMinutes: number = 120) => {
  const retryCounts = new Map();
  const maxInactiveMilliseconds = maxInactiveMinutes * 60 * 1000;

  axios.interceptors.request.use(
    async (config) => {
      const now = Date.now();
      const lastActivity = localStorage.getItem(LAST_ACTIVITY_TIMESTAMP_KEY);
      const lastActivityTimestamp = Number(lastActivity);
      const isInactiveLimitExceeded =
        !!lastActivity && !Number.isNaN(lastActivityTimestamp)
          ? now - lastActivityTimestamp > maxInactiveMilliseconds
          : false;

      if (isInactiveLimitExceeded) {
        await clearUser();
        window.location.reload();

        return Promise.reject(new Error('Session expired due to inactivity'));
      }

      localStorage.setItem(LAST_ACTIVITY_TIMESTAMP_KEY, now.toString());
      return config;
    },
    (error) => Promise.reject(error),
  );

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401 && error.config) {
        const requestId = error.config.url + error.config.method;

        const currentRetries = retryCounts.get(requestId) || 0;

        if (currentRetries < 3) {
          retryCounts.set(requestId, currentRetries + 1);
          const isSuccessRefresh = await refreshTokenSingleFlight();

          if (isSuccessRefresh && applyAuthHeaderFromStorage(error.config)) {
            return await axios(error.config);
          }

          await clearUser();
          window.location.reload();
        }
      }
      return Promise.reject(error);
    },
  );
};

export const storeRefreshToken = (refreshToken: string) => {
  try {
    const path = '/Account/ConnectCognitoUserToBeproUser';

    axios.post(path, { refreshToken });
  } catch (error) {
    console.error(error);
  }
};

export const disconnectToken = async () => {
  try {
    const path = '/Account/DisconnectCognitoUserFromBeproUser';

    return (await axios.post(path)).data;
  } catch (error) {
    console.error(error);
  }
};

export const getGtprCredentials = async (): Promise<GtprCredentialsResponse | undefined> => {
  const gtprCred = localStorage.getItem('gtprCredentials');
  if (gtprCred) {
    return JSON.parse(gtprCred);
  }

  try {
    const path = 'Account/GetGtprCredentials';
    const res = await axios.get(path);
    localStorage.setItem('gtprCredentials', JSON.stringify(res.data));
    return res.data;
  } catch {
    return undefined;
  }
};

export const setGlobalsManager = (isManager: boolean) => {
  const globals = localStorage.getItem('globals');
  if (globals) {
    const globalsObj = JSON.parse(globals);
    globalsObj.isManager = isManager;
    localStorage.setItem('globals', JSON.stringify(globalsObj));
  }
};

export const redirectToBeproCognitoLogin = async () => {
  localStorage.removeItem('user');
  const gtprCred = await getGtprCredentials();
  if (!gtprCred) {
    return;
  }

  const globals = localStorage.getItem('globals');
  if (!globals) {
    return;
  }
  const globalsObj = JSON.parse(globals);
  const appId = globalsObj.isManager ? globalsObj.managerAppId : globalsObj.appId;

  const url = new URL(loginHubUrl);
  url.searchParams.set('action', 'logout');
  url.searchParams.set('app_id', appId);
  url.searchParams.set(
    'redirect_uri',
    process.env.REACT_APP_DEBUG ? 'http://localhost:3003' : gtprCred.redirectUri,
  );
  clearUser();
  window.location.href = url.toString();
};

export const redirectToLogin = async () => {
  localStorage.removeItem('user');
  const gtprCred = await getGtprCredentials();
  if (!gtprCred) {
    return;
  }
  const isManager = window.location.pathname === '/manager-login';
  const redirectUri = process.env.REACT_APP_DEBUG ? 'http://localhost:3003' : gtprCred.redirectUri;
  const loginUrl =
    `${isManager ? gtprCred.managerCognitoDomain : gtprCred.cognitoDomain}/login` +
    `?response_type=code` +
    `&client_id=${encodeURIComponent(isManager ? gtprCred.managerClientId : gtprCred.clientId)}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}`;

  setGlobalsManager(isManager);

  window.location.href = loginUrl;
};

const getToken = async (code: string) => {
  const gtprCred = await getGtprCredentials();
  if (!gtprCred) {
    return;
  }

  const globals = localStorage.getItem('globals');
  if (!globals) {
    return;
  }
  const globalsObj = JSON.parse(globals);
  const isManager = globalsObj.isManager;
  const client_id = isManager ? gtprCred.managerClientId : gtprCred.clientId;
  const cognitoDomain = isManager ? gtprCred.managerCognitoDomain : gtprCred.cognitoDomain;
  const tokenUrl = `${cognitoDomain}/oauth2/token`;

  const redirectUri = process.env.REACT_APP_DEBUG ? 'http://localhost:3003' : gtprCred.redirectUri;
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id,
    code: code,
    redirect_uri: redirectUri,
  });

  const res = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  });

  const resData = await res.json();
  axios.defaults.headers.common['Authorization'] = resData.token_type + ' ' + resData.access_token;
  const storeData = {
    ...resData,
    expiresIn: new Date(new Date().getTime() + resData.expires_in * 1000),
  };
  localStorage.setItem('cognito', JSON.stringify(storeData));
  return storeData;
};

export const handleBeproCognitoToken = async () => {
  const user = localStorage.getItem('user');
  if (user) {
    const userData = JSON.parse(user);
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + userData.accessToken;
    return userData;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const returnedState = urlParams.get('state');
  if (!code || !returnedState) return;
  const storedState = sessionStorage.getItem(`userPkce_state`);
  const verifier = sessionStorage.getItem(`userPkce_verifier`);
  if (!storedState || !verifier || storedState !== returnedState) {
    return;
  }
  sessionStorage.removeItem(`userPkce_state`);
  sessionStorage.removeItem(`userPkce_verifier`);

  const HUB_API_URL = `${loginHubUrl}/api`;
  const res = await axios.post(`${HUB_API_URL}/auth/token`, { code, codeVerifier: verifier });

  if (res.data.accessToken && res.data.idToken) {
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + res.data.accessToken;

    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    window.history.replaceState({}, '', url.toString());

    storeRefreshToken(res.data.refreshToken);

    const user = await getUserInfo(true);

    const userData = {
      ...user,
      accessToken: res.data.accessToken,
      refreshToken: res.data.refreshToken,
      cognitoUsername: res.data.cognitoUsername,
    };
    localStorage.setItem('user', JSON.stringify(userData));
    return userData;
  }
};

export async function handleAzureCallback() {
  const user = localStorage.getItem('user');
  if (user) {
    const userData = JSON.parse(user);
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + userData.accessToken;
    return userData;
  }
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const returnedState = urlParams.get('state');
  if (!code || !returnedState) return;
  const savedState = sessionStorage.getItem('userPkce_state');
  const verifier = sessionStorage.getItem('userPkce_verifier');

  if (!savedState || returnedState !== savedState) {
    return;
  }
  if (!verifier) {
    return;
  }

  const gtprCred = await getGtprCredentials();
  if (!gtprCred) {
    return;
  }

  const redirectUri = process.env.REACT_APP_DEBUG ? 'http://localhost:3003' : gtprCred.redirectUri;

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: gtprCred.clientId,
    redirect_uri: redirectUri,
    code,
    code_verifier: verifier,
  });

  const response = await fetch(`${gtprCred.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    return;
  }

  const resData = await response.json();

  sessionStorage.removeItem('userPkce_state');
  sessionStorage.removeItem('userPkce_verifier');

  axios.defaults.headers.common['Authorization'] = resData.token_type + ' ' + resData.access_token;

  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  window.history.replaceState({}, '', url.toString());

  storeRefreshToken(resData.refresh_token);

  const userInfo = await getUserInfo(true);
  const userData = {
    ...userInfo,
    accessToken: resData.access_token,
    refreshToken: resData.refresh_token,
  };
  localStorage.setItem('user', JSON.stringify(userData));
  return userData;
}

export const handleCognitoToken = async (needDefaultUser?: boolean, dontRedirect?: boolean) => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const cognito = localStorage.getItem('cognito');
  if (cognito && JSON.parse(cognito).access_token) {
    const resData = JSON.parse(cognito);
    axios.defaults.headers.common['Authorization'] =
      resData.token_type + ' ' + resData.access_token;
    const user = await getUserInfo(true);
    if (user) {
      return user;
    }
  }

  if (code) {
    const resData = await getToken(code);
    storeRefreshToken(resData.refresh_token);

    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    window.history.replaceState({}, '', url.toString());

    const user = await getUserInfo(true);
    return user;
  } else if (needDefaultUser) {
    const user = await getDefaultUser();
    return user;
  } else if (!dontRedirect) {
    await redirectToLogin();
  }
};

const getUserInfo = async (isAuth: boolean, isManager: boolean = false) => {
  const urlUser = 'Account/GetUserInfo';
  const user = localStorage.getItem('user');
  if (user) {
    return JSON.parse(user);
  }

  try {
    const userConfig = (await getUserConfig({ base: {}, hotel: {} })) || {};
    const dataUser = (await axios.get(urlUser)).data;
    const user = {
      ...dataUser,
      isAuth,
      config: userConfig,
      isManager,
    };

    localStorage.setItem('user', JSON.stringify(user));
    return user;
  } catch (error) {
    return undefined;
  }
};

const getDefaultUser = async (): Promise<User | undefined> => {
  const res = await connectToDefaultUser();

  if (res?.cognitoSigninResponse?.result?.idToken) {
    axios.defaults.headers.common['Authorization'] =
      res.cognitoSigninResponse.result.tokenType + ' ' + res.cognitoSigninResponse.result.idToken;

    const cognitoJson = {
      refresh_token: res.cognitoSigninResponse.result.refreshToken,
      expiresIn: new Date(new Date().getTime() + res.cognitoSigninResponse.result.expiresIn * 1000),
      access_token: res.cognitoSigninResponse.result.idToken,
      token_type: res.cognitoSigninResponse.result.tokenType,
    };
    localStorage.setItem('cognito', JSON.stringify(cognitoJson));

    const user = await getUserInfo(false);
    return user;
  }
};

export const logOut = async (needDefaultUser?: boolean) => {
  const response = await disconnectToken();

  if (needDefaultUser) {
    showSuccess('Logged out successfully');
    return getDefaultUser();
  } else if (response?.redirectUrl) {
    clearUser();
    window.location.href = response.redirectUrl;
  }
};

export const clearUser = () => {
  const cookies = localStorage.getItem('cookie-policy-result');
  const globals = localStorage.getItem('globals');
  const storageVersion = localStorage.getItem('storage-version');
  const storedState = sessionStorage.getItem(`userPkce_state`);
  const verifier = sessionStorage.getItem(`userPkce_verifier`);
  localStorage.clear();
  sessionStorage.clear();

  if (storageVersion) {
    localStorage.setItem('storage-version', storageVersion);
  }

  if (cookies) {
    localStorage.setItem('cookie-policy-result', cookies);
  }
  if (globals) {
    localStorage.setItem('globals', globals);
  }
  if (storedState) {
    sessionStorage.setItem(`userPkce_state`, storedState);
  }
  if (verifier) {
    sessionStorage.setItem(`userPkce_verifier`, verifier);
  }
  axios.defaults.headers.common['Authorization'] = '';
};

export const refreshToken = async () => {
  try {
    const globals = localStorage.getItem('globals');
    if (!globals) {
      return;
    }
    const globalsObj = JSON.parse(globals);

    if (globalsObj.useAzureAD && !globalsObj.isManager) {
      const user = localStorage.getItem('user');
      if (!user) {
        return;
      }
      const userData = JSON.parse(user);
      if (!userData.refreshToken) {
        return;
      }

      const gtprCred = await getGtprCredentials();
      if (!gtprCred) {
        return;
      }

      const tokenUrl = `${gtprCred.cognitoDomain}/oauth2/token`;
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: gtprCred.clientId,
        refresh_token: userData.refreshToken,
      });

      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      });

      if (!response.ok) {
        return;
      }

      const responseData = await response.json();
      axios.defaults.headers.common['Authorization'] =
        responseData.token_type + ' ' + responseData.access_token;

      const newUser = {
        ...userData,
        accessToken: responseData.access_token,
      };
      localStorage.setItem('user', JSON.stringify(newUser));
      return true;
    }

    if (globalsObj.useBeproCognito) {
      const user = localStorage.getItem('user');
      if (!user) {
        return;
      }
      const userData = JSON.parse(user);

      const response = await fetch(`${loginHubUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: userData.refreshToken,
          appId: globalsObj.isManager ? globalsObj.managerAppId : globalsObj.appId,
          cognitoUsername: userData.cognitoUsername,
        }),
      });

      if (!response.ok) {
        return;
      }

      const responseData = await response.json();

      if (responseData.accessToken && responseData.idToken) {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + responseData.accessToken;

        const newUser = {
          ...userData,
          accessToken: responseData.accessToken,
          idToken: responseData.idToken,
        };
        localStorage.setItem('user', JSON.stringify(newUser));
        return true;
      }
      return;
    }

    const cognitoData = localStorage.getItem('cognito');
    const refreshTokenValue = cognitoData ? JSON.parse(cognitoData).refresh_token : undefined;

    const gtprCred = await getGtprCredentials();
    if (!gtprCred || !refreshTokenValue) {
      return;
    }

    const user = localStorage.getItem('user');
    const isManager = user ? JSON.parse(user).branchId === 5000 : false;
    const cognitoDomain = isManager ? gtprCred.managerCognitoDomain : gtprCred.cognitoDomain;
    const client_id = isManager ? gtprCred.managerClientId : gtprCred.clientId;
    const tokenUrl = `${cognitoDomain}/oauth2/token`;
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id,
      refresh_token: refreshTokenValue,
    });

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      return;
    }

    const responseData = await response.json();

    axios.defaults.headers.common['Authorization'] =
      responseData.token_type + ' ' + responseData.access_token;

    const newStoreData = {
      refresh_token: refreshTokenValue,
      access_token: responseData.access_token,
      token_type: responseData.token_type,
      expiresIn: new Date(new Date().getTime() + responseData.expires_in * 1000),
    };

    localStorage.setItem('cognito', JSON.stringify(newStoreData));
    return true;
  } catch (error) {
    console.log('Error refreshing token:', error);
  }
};
